import { forwardRef, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { changePassword } from "@/api/auth";
import { cn } from "@/lib/utils";

const schema = z
  .object({
    current_password: z.string().min(1, "Required"),
    new_password: z.string().min(8, "At least 8 characters"),
    confirm_password: z.string().min(1, "Required"),
  })
  .refine((v) => v.new_password === v.confirm_password, {
    path: ["confirm_password"],
    message: "Passwords do not match",
  });

type FormValues = z.infer<typeof schema>;

export function PasswordSection() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
    watch,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const newPassword = watch("new_password");
  const strength = useMemo(() => scorePassword(newPassword), [newPassword]);

  async function onSubmit(values: FormValues) {
    try {
      await changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      toast.success("Password updated");
      reset();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        setError("current_password", {
          message: "Current password is incorrect",
        });
        return;
      }
      const detail = axios.isAxiosError(err)
        ? (err.response?.data as { detail?: unknown } | undefined)?.detail
        : null;
      toast.error(typeof detail === "string" ? detail : "Couldn't update password");
    }
  }

  return (
    <section className="rounded-plane bg-white p-6 shadow-plane">
      <h2 className="text-lg font-medium tracking-tight text-slate-900">
        Password
      </h2>
      <p className="mt-1 text-sm text-slate-500">Change your password</p>

      <form
        className="mt-5 max-w-md space-y-4"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <PasswordField
          label="Current password"
          autoComplete="current-password"
          show={showCurrent}
          onToggle={() => setShowCurrent((v) => !v)}
          error={errors.current_password?.message}
          {...register("current_password")}
        />
        <div>
          <PasswordField
            label="New password"
            autoComplete="new-password"
            show={showNew}
            onToggle={() => setShowNew((v) => !v)}
            error={errors.new_password?.message}
            {...register("new_password")}
          />
          {newPassword.length > 0 && <StrengthMeter score={strength} />}
        </div>
        <PasswordField
          label="Confirm new password"
          autoComplete="new-password"
          show={showConfirm}
          onToggle={() => setShowConfirm((v) => !v)}
          error={errors.confirm_password?.message}
          {...register("confirm_password")}
        />

        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
          className="h-10 rounded-lg"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              Updating…
            </>
          ) : (
            "Update password"
          )}
        </Button>
      </form>
    </section>
  );
}

interface PasswordFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  show: boolean;
  onToggle: () => void;
  error?: string;
}

const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField(
    { label, show, onToggle, error, className, ...props },
    ref,
  ) {
    return (
      <div>
        <label className="block text-sm font-medium text-slate-700">
          {label}
        </label>
        <div className="relative mt-1">
          <Input
            ref={ref}
            type={show ? "text" : "password"}
            aria-invalid={!!error}
            className={cn(
              "h-10 rounded-lg pr-10",
              error && "border-rose-400 focus-visible:ring-rose-300",
              className,
            )}
            {...props}
          />
          <button
            type="button"
            onClick={onToggle}
            tabIndex={-1}
            aria-label={show ? "Hide password" : "Show password"}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-700"
          >
            {show ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
        {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
      </div>
    );
  },
);

type Strength = "weak" | "medium" | "strong";

function scorePassword(pw: string): Strength {
  if (!pw) return "weak";
  const hasLetter = /[a-zA-Z]/.test(pw);
  const hasNumber = /\d/.test(pw);
  const hasSymbol = /[^a-zA-Z0-9]/.test(pw);
  if (pw.length >= 14 && hasLetter && hasNumber && hasSymbol) return "strong";
  if (pw.length >= 10 && hasLetter && hasNumber) return "medium";
  return "weak";
}

function StrengthMeter({ score }: { score: Strength }) {
  const config = {
    weak: { label: "Weak", color: "bg-rose-500", filled: 1 },
    medium: { label: "Medium", color: "bg-amber-500", filled: 2 },
    strong: { label: "Strong", color: "bg-emerald-500", filled: 3 },
  }[score];

  return (
    <div className="mt-2 space-y-1">
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={cn(
              "h-1 flex-1 rounded-full transition-colors",
              i < config.filled ? config.color : "bg-slate-200",
            )}
          />
        ))}
      </div>
      <p
        className={cn(
          "text-[11px] font-medium",
          score === "weak"
            ? "text-rose-600"
            : score === "medium"
              ? "text-amber-700"
              : "text-emerald-700",
        )}
      >
        {config.label}
      </p>
    </div>
  );
}
